import React, { useEffect, useState } from 'react';
import { Alert, Flex } from 'antd';
import { getMinExtensionVersion } from '@ext/services/forceUpdate';
import { getSyncEngine } from '@ext/services/syncEngine';
import { extensionVersion, versionAtLeast } from '../extensionBridge';
import { useExtensionInstalled } from '../useExtensionInstalled';
import { GetExtension } from './ReviewInExtension';

const DISMISSED_KEY = 'lingoping_install_banner_dismissed';

function wasDismissed(): boolean {
  try {
    return window.sessionStorage.getItem(DISMISSED_KEY) === '1';
  } catch {
    return false;
  }
}

function rememberDismissed(): void {
  try {
    window.sessionStorage.setItem(DISMISSED_KEY, '1');
  } catch {
    // The banner is only hidden until the page is reloaded.
  }
}

/**
 * Shown above the dashboard to a visitor whose browser has no LingoPing extension: what it adds,
 * and where to get it. Hidden while the check runs, and for the rest of the tab's session once
 * dismissed.
 */
/**
 * The installed extension's version when it is older than the server works with (the server
 * names its minimum in every sync answer); null otherwise.
 */
function useOutdatedExtension(): string | null {
  const [outdated, setOutdated] = useState<string | null>(null);
  useEffect(() => {
    let current = true;
    const check = async () => {
      const [min, version] = await Promise.all([getMinExtensionVersion(), extensionVersion()]);
      if (current) setOutdated(min && version && !versionAtLeast(version, min) ? version : null);
    };
    void check();
    const off = getSyncEngine().onSent(() => void check());
    return () => {
      current = false;
      off();
    };
  }, []);
  return outdated;
}

export const InstallBanner: React.FC<{ storeUrl?: string }> = ({ storeUrl }) => {
  const installed = useExtensionInstalled();
  const outdated = useOutdatedExtension();
  const [dismissed, setDismissed] = useState(wasDismissed);

  if (installed && outdated) {
    return (
      <div
        style={{
          maxWidth: 1200,
          margin: '0 auto',
          padding: '16px 24px 0',
          boxSizing: 'border-box',
          width: '100%',
        }}
      >
        <Alert
          type="warning"
          showIcon
          title="Your LingoPing extension needs an update"
          description={`Version ${outdated} no longer works with LingoPing. Chrome updates it by itself within a few hours; to do it now, open the extension and choose “Update now”.`}
        />
      </div>
    );
  }

  if (installed !== false || dismissed) return null;

  return (
    <div
      style={{
        maxWidth: 1200,
        margin: '0 auto',
        padding: '16px 24px 0',
        boxSizing: 'border-box',
        width: '100%',
      }}
    >
      <Alert
        type="info"
        showIcon
        title="Add the LingoPing extension to your browser"
        description={
          <Flex vertical gap={8} align="flex-start">
            <span>Get the extension to review your words and add new ones while you browse.</span>
            <GetExtension storeUrl={storeUrl} />
          </Flex>
        }
        closable={{
          'aria-label': 'Dismiss',
          onClose: () => {
            rememberDismissed();
            setDismissed(true);
          },
        }}
      />
    </div>
  );
};
